import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useGroup } from '../context/GroupContext';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import groupService from '../api/groupService';
import calendarApi from '../services/calendarApi';
import WaitlistSuccessModal from './WaitlistSuccessModal';
import useWaitlistModal from '../hooks/useWaitlistModal';
import '../styles/AdminDashboard.css';

// Import the individual content components
import EventsList from './group/EventsList';
import ClassesList from './group/ClassesList';
import CommunityCalendar from './group/CommunityCalendar';
import UpdatesList from './group/UpdatesList';
import Prayer from './group/Prayer';
import CharityBox from './group/CharityBox';
import MembersContent from './MembersContent';
import AdminProfilePage from './AdminProfilePage';
import CreateEventModal from './group/CreateEventModal';
import CreateClassModal from './group/CreateClassModal';

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const { adminGroup, contextLoading } = useGroup();
  const location = useLocation();
  const navigate = useNavigate();
  const { showWaitlistModal, closeWaitlistModal } = useWaitlistModal();
  
  const [activeView, setActiveView] = useState('members');
  const [memberCount, setMemberCount] = useState(0);
  const [showFounderDealBanner, setShowFounderDealBanner] = useState(false);
  const [groupData, setGroupData] = useState({
    events: [],
    classes: [],
    prayer: [],
    members: []
  });
  const [calendars, setCalendars] = useState([]);
  const [activeCalendar, setActiveCalendar] = useState(null);
  const [calendarsLoading, setCalendarsLoading] = useState(true);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [showCreateClassModal, setShowCreateClassModal] = useState(false);

  // Show existing dashboard with empty states when adminGroup is not loaded yet
  // This allows immediate display without complex onboarding flows
  const effectiveGroup = adminGroup || { id: null, name: currentUser?.user_metadata?.group_name || 'Your Community' };

  // Fetch calendars for the group
  useEffect(() => {
    const fetchCalendars = async () => {
      const groupId = adminGroup?.id;
      
      if (!groupId) {
        setCalendarsLoading(false);
        return;
      }
      
      setCalendarsLoading(true);
      
      try {
        const response = await calendarApi.getCalendars(groupId);
        let calendarsData = response.data || [];
        
        // Sort calendars: default first, then by display_order
        calendarsData = calendarsData.sort((a, b) => {
          if (a.is_default && !b.is_default) return -1;
          if (!a.is_default && b.is_default) return 1;
          return (a.display_order || 0) - (b.display_order || 0);
        });
        
        setCalendars(calendarsData);
        
        // Set active calendar to default or first calendar
        const defaultCalendar = calendarsData.find(c => c.is_default) || calendarsData[0];
        setActiveCalendar(defaultCalendar?.id || null);
      } catch (err) {
        console.error('Error fetching calendars:', err);
      } finally {
        setCalendarsLoading(false);
      }
    };

    fetchCalendars();
  }, [adminGroup?.id]);

    // Extract active view from URL path (route-based navigation)
  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    
    // Map URL segments to views
    const viewMapping = {
      'members': 'members',
      'events': 'events', 
      'classes': 'classes',
      'calendar': 'calendar',
      'prayer': 'prayer',
      'updates': 'updates',
      'memberships': 'memberships',
      'account': 'account'
    };
    
    if (viewMapping[lastSegment]) {
      setActiveView(viewMapping[lastSegment]);
    } else if (location.pathname === '/admin/dashboard') {
      // Redirect to members page as default landing
      navigate('/admin/dashboard/members', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Check if we should show Founder Deal snackbar and fetch member count
  useEffect(() => {
    const checkFounderDeal = async () => {
      // Only check if user has a community and it's loaded
      if (!adminGroup?.id || contextLoading) return;
      
      try {
        // Only show for Founder Deal groups
        if (!adminGroup.is_founder_deal) return;
        
        // Fetch current member count
        const response = await groupService.getGroupMembersWithDetails(adminGroup.id);
        const count = response?.data?.length || 0;
        setMemberCount(count);
        
        // Always show banner when on members page if under 25 members
        if (count < 25 && activeView === 'members') {
          setShowFounderDealBanner(true);
        }
      } catch (error) {
        console.error('Error checking founder deal status:', error);
      }
    };

    checkFounderDeal();
  }, [adminGroup, contextLoading, activeView]); // Added activeView to dependencies

  // Load data for the current view (removed blocking loading state)
  useEffect(() => {
    if (!adminGroup) return;
    
    const loadViewData = async () => {
      // Removed setLoading(true) to prevent full page loading
      try {
        switch (activeView) {
          case 'events':
            if (!groupData.events.length) {
              const eventsData = await groupService.getGroupEvents(adminGroup.id);
              // Ensure we always have an array
              const eventsArray = Array.isArray(eventsData) ? eventsData : 
                                 (eventsData && Array.isArray(eventsData.data)) ? eventsData.data : [];
              setGroupData(prev => ({ ...prev, events: eventsArray }));
            }
            break;
          case 'classes':
            if (!groupData.classes.length) {
              const classesData = await groupService.getGroupClasses(adminGroup.id);
              // Ensure we always have an array
              const classesArray = Array.isArray(classesData) ? classesData : 
                                  (classesData && Array.isArray(classesData.data)) ? classesData.data : [];
              setGroupData(prev => ({ ...prev, classes: classesArray }));
            }
            break;
          case 'prayer':
            if (!groupData.prayer.length) {
              const prayerData = await groupService.getGroupPrayer(adminGroup.id);
              setGroupData(prev => ({ ...prev, prayer: prayerData || [] }));
            }
            break;
          default:
            break;
        }
      } catch (error) {
        console.error(`Error loading ${activeView} data:`, error);
      }
      // Removed setLoading(false) since we're not setting loading to true
    };

    loadViewData();
  }, [activeView, adminGroup]);

  // Handle creating new events/classes
  const handleCreateNew = () => {
    if (activeView === 'events') {
      setShowCreateEventModal(true);
    } else if (activeView === 'classes') {
      setShowCreateClassModal(true);
    }
  };

  // Handle successful creation
  const handleEventCreated = async () => {
    setShowCreateEventModal(false);
    // Show success toast
    if (window.showToast) {
      window.showToast('Event created successfully!', 'success');
    }
    // Refresh events data
    if (adminGroup) {
      const eventsData = await groupService.getGroupEvents(adminGroup.id);
      const eventsArray = Array.isArray(eventsData) ? eventsData : 
                         (eventsData && Array.isArray(eventsData.data)) ? eventsData.data : [];
      setGroupData(prev => ({ ...prev, events: eventsArray }));
    }
  };

  const handleClassCreated = async () => {
    setShowCreateClassModal(false);
    // Show success toast
    if (window.showToast) {
      window.showToast('Class created successfully!', 'success');
    }
    // Refresh classes data
    if (adminGroup) {
      const classesData = await groupService.getGroupClasses(adminGroup.id);
      const classesArray = Array.isArray(classesData) ? classesData : 
                          (classesData && Array.isArray(classesData.data)) ? classesData.data : [];
      setGroupData(prev => ({ ...prev, classes: classesArray }));
    }
  };

  const renderContent = () => {
    // Safety check - if no adminGroup is loaded, show loading or fallback
    if (!adminGroup) {
      return (
        <div className="admin-content-section">
          <div className="loading-message">
            <p>Loading community data...</p>
          </div>
        </div>
      );
    }

    switch (activeView) {
      case 'events':
        return (
          <div className="admin-content-section">
            <EventsList 
              groupId={adminGroup.id} 
              searchQuery="" 
              isAdmin={true}
            />
          </div>
        );
      
      case 'classes':
        return (
          <div className="admin-content-section">
            <ClassesList 
              groupId={adminGroup.id} 
              searchQuery="" 
              isAdmin={true}
            />
          </div>
        );
      
      case 'calendar':
        return (
          <div className="admin-content-section">
            <CommunityCalendar 
              groupId={adminGroup.id} 
              isAdmin={true}
            />
          </div>
        );
      
      case 'prayer':
        return (
          <div className="admin-content-section">
            <Prayer 
              groupId={adminGroup.id} 
              groupName={adminGroup.name}
              searchQuery="" 
              isAdmin={true}
            />
          </div>
        );
      
      case 'updates':
        return (
          <div className="admin-content-section">
            <UpdatesList 
              groupId={adminGroup.id} 
              searchQuery="" 
              isAdmin={true}
            />
          </div>
        );
      
      case 'memberships':
        return (
          <div className="admin-content-section">
            <div className="section-header">
              <h2 className="text-2xl font-bold text-gray-900">Memberships</h2>
              <div className="coming-soon-banner">
                <div className="bg-gradient-to-r from-blue-500 to-teal-500 text-white p-6 rounded-lg text-center">
                  <h3 className="text-xl font-semibold mb-2">Coming Soon!</h3>
                  <p>Membership management feature is currently in development.</p>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'members':
        return (
          <div className="admin-content-section">
            <MembersContent groupId={adminGroup.id} />
          </div>
        );
      
      case 'account':
        return (
          <div className="admin-content-section">
            <AdminProfilePage />
          </div>
        );
      
      default:
        return (
          <div className="admin-content-section">
            <div className="section-header">
              <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
            </div>
            <div className="empty-state">
              <p className="text-gray-500">Select an option from the sidebar to get started.</p>
            </div>
          </div>
        );
    }
  };

  return (
    <AdminLayout>
      {/* Shluchim Deal Activation Banner - Top Position */}
      {showFounderDealBanner && memberCount < 25 && activeView === 'members' && (
        <div className="border-b border-gray-200 bg-blue-50 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">Shluchim Deal:</span>
                <span className="text-sm text-gray-700">
                  Invite {25 - memberCount} more {25 - memberCount === 1 ? 'member' : 'members'} to activate 50% off Premium for life
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-32 bg-gray-200 rounded-full h-1.5">
                  <div 
                    className="bg-blue-600 h-full rounded-full transition-all duration-300"
                    style={{ width: `${(memberCount / 25) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 font-medium">{memberCount}/25</span>
                <span className="text-xs text-gray-500">• Due Dec 22</span>
              </div>
            </div>
            <button 
              onClick={() => setShowFounderDealBanner(false)}
              className="text-gray-400 hover:text-gray-600 ml-4"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      {renderContent()}
      
      {/* Create Event Modal */}
      <CreateEventModal
        isOpen={showCreateEventModal}
        onClose={() => setShowCreateEventModal(false)}
        groupId={effectiveGroup?.id || adminGroup?.id}
        calendars={calendars}
        activeCalendar={activeCalendar}
        calendarsLoading={calendarsLoading}
        onEventUpdated={handleEventCreated}
        isEditMode={false}
        hideCalendarSelector={true}
      />
      
      {/* Create Class Modal */}
      <CreateClassModal
        isOpen={showCreateClassModal}
        onClose={() => setShowCreateClassModal(false)}
        groupId={effectiveGroup?.id || adminGroup?.id}
        calendars={calendars}
        activeCalendar={activeCalendar}
        calendarsLoading={calendarsLoading}
        onClassUpdated={handleClassCreated}
        isEditMode={false}
        hideCalendarSelector={true}
      />
      
      {/* Waitlist Success Modal */}
      <WaitlistSuccessModal
        isOpen={showWaitlistModal}
        onClose={closeWaitlistModal}
      />
    </AdminLayout>
  );
};

export default AdminDashboard;
